import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../auth.service';

// Two responsibilities, scoped to the Account API specifically (login/session/
// logout live under /api/Account/) - NOT every endpoint the app calls. This app
// also talks to a completely separate "engine" backend (a different origin, see
// EnvironmentService.activeEngineBaseUrl) that has nothing to do with the session
// cookie and whose own CORS policy doesn't allow credentials; attaching
// withCredentials there breaks its preflight for no benefit, since it never reads
// this cookie. Static/relative fetches (e.g. AppEnvService.load()'s
// /assets/environment.json during bootstrap) are also same-origin already, so
// they never need this - matching by path also means never touching
// EnvironmentService/AuthService for those, both of which depend on that same
// bootstrap load having completed.
//
// 1. Sends/receives the HttpOnly session cookie on every /api/Account/ request.
//    The Angular dev server and the DataTransformerAPI run on different origins,
//    so without this the browser would neither send the cookie nor accept the
//    Set-Cookie response.
//
// 2. Reacts to a 401 from an /api/Account/ call by treating the session as gone:
//    clears local auth state and notifies every other tab (via AuthService). A
//    401 from login itself is just a rejected credentials attempt, not a session
//    going invalid mid-use, so it's left alone here (session/status never 401s
//    at all - see AccountController.GetSessionStatus - but is still excluded
//    below for clarity/defensiveness).
@Injectable()
export class CredentialsInterceptor implements HttpInterceptor {
  // AuthService is deliberately NOT constructor-injected. HTTP_INTERCEPTORS are
  // constructed the first time ANY HttpClient call is made - and the very first
  // call in this app's life is AppEnvService.load() fetching
  // /assets/environment.json during bootstrap. Constructor-injecting AuthService
  // here would force AuthService -> UserService -> EnvironmentService to
  // construct before that same config fetch has completed, which is exactly
  // what crashes EnvironmentService's constructor (it reads AppEnvService's
  // config synchronously) - deterministically, on every load, not just a race.
  // Injector.get() defers that construction to the first actual 401 on an
  // Account call, by which point bootstrap has long since finished. For the same
  // reason, intercept() below decides everything from the request URL string,
  // never from EnvironmentService - reading activeAppBaseUrl to compare against
  // would construct EnvironmentService on that same first bootstrap call too.
  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isAccountUrl(request.url)) {
      return next.handle(request);
    }

    const credentialedRequest = request.clone({ withCredentials: true });

    return next.handle(credentialedRequest).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && !this.isSessionCheckUrl(request.url)) {
          this.handleUnauthorized();
        }
        return throwError(() => error);
      })
    );
  }

  private isAccountUrl(url: string): boolean {
    return url.includes('/api/Account/');
  }

  private isSessionCheckUrl(url: string): boolean {
    return url.includes('/api/Account/session/status') || url.includes('/api/Account/login');
  }

  private handleUnauthorized(): void {
    const authService = this.injector.get(AuthService);
    // Already known logged-out (e.g. a background call firing before the user
    // ever logged in, or a second 401 arriving right after the first) - nothing
    // to do, and importantly nothing to re-broadcast.
    if (!authService.getLoginStatus()) {
      return;
    }
    // Reuses the same full cleanup as an explicit logout (releases refinery
    // presence, clears local state, tells the server, notifies other tabs) -
    // the server call is a harmless no-op if the session is already gone.
    void authService.logout();
  }
}
